import { useAtom } from 'jotai'
import { useCallback, TouchEventHandler, useMemo, useState, useEffect } from 'react'
import useAtomWithRef from '../../../../utils/useAtomWithRef'
// import { formatTime } from '../../../../utils/videojs'
import {
  ownsNFTAtom,
  seekModeAtom,
  timelineExcludedVisibilityAtom,
  videoElementAtom,
  wControlsVisibilityAtom,
  wVisibilityTimerIdAtom,
} from '../atoms'

//* =================================== Types ==================================

type MobileTimelineSeekerProps = {
  reference: React.RefObject<HTMLDivElement>
}

//* =============================== React Component ============================

const MobileTimelineSeeker = ({ reference }: MobileTimelineSeekerProps): JSX.Element => {
  const [videoElementRef, videoElement] = useAtomWithRef(videoElementAtom)
  const [, setControlsExcludedTimelineVisible] = useAtom(timelineExcludedVisibilityAtom)
  const [seekModeRef, _, setSeekMode] = useAtomWithRef(seekModeAtom)
  const [, setControlsVisible] = useAtom(wControlsVisibilityAtom)
  const [, setVisibilityTimerId] = useAtom(wVisibilityTimerIdAtom)
  const [ownsNFT] = useAtom(ownsNFTAtom)

  const [seekProgress, setSeekProgress] = useState(0.0)

  const [anchorLeft, anchorWidth] = useMemo(() => {
    if (reference.current) {
      const rect = reference.current.getBoundingClientRect()
      return [rect.left, rect.width]
    }
    return [0, 0]
  }, [reference.current])

  useEffect(() => {
    if (!videoElement) return
    videoElement.addEventListener('timeupdate', () => {
      if (!seekModeRef.current) {
        setSeekProgress(videoElement.currentTime / videoElement.duration)
      }
    })
  }, [videoElement])

  // ------------------------ Callbacks ----------------------------------------

  const handleTouchStart = useCallback(() => {
    // Disable updates from the player regarding the progress.
    setSeekMode(true)
    // Force other control elements to hide themselves.
    setControlsExcludedTimelineVisible(false)

    setVisibilityTimerId('cancel')
  }, [])

  // When the user moves the seeker around, we don't want to update the
  // current time.
  const handleTouchMove = useCallback<TouchEventHandler<HTMLDivElement>>(
    (event) => {
      // Extract touch.
      const touch = event.touches[0] || event.changedTouches[0]
      const x = touch.pageX

      // Print all variables above to the console
      const newProgress = Math.max(0, Math.min((x - anchorLeft) / anchorWidth, 1))

      setSeekProgress(newProgress)
    },
    [anchorLeft, anchorWidth]
  )

  const handleTouchEnd = useCallback<TouchEventHandler<HTMLDivElement>>(
    (event) => {
      // Extract touch.
      const touch = event.touches[0] || event.changedTouches[0]
      const x = touch.pageX

      // Calculate new progress.
      const newProgress = (x - anchorLeft) / anchorWidth

      // Set time based on progress.
      videoElementRef.current!.currentTime = newProgress * videoElement!.duration

      // Let the player update the progress of the progress bar again.
      setSeekMode(false)

      // Allows other control items to get visible again.
      setControlsExcludedTimelineVisible(true)

      // Make controls actually visible again.
      setControlsVisible(true)

      setVisibilityTimerId('restart')
    },
    [anchorLeft, anchorWidth, videoElement]
  )

  return (
    <>
      {
        // Due to high frequency changes of that style, this template string
        // must not be a styled component. They are simply not made for that.
      }
      <style>
        {`.seeker-wrapper {
            position: relative;
            transform: translate3d(-50%, 0, 0);
            opacity: 1;
            z-index: 3;
            will-change: transform;
            overflow: hidden;
            padding: 10px;

        }
        `}
      </style>
      <style>
        {`.seeker {
          width: 14px;
          height: 14px;
          border-radius: 100%;
          background: ${ownsNFT ? 'white' : 'white'};
          overflow: hidden;
        }`}
      </style>
      <div
        className="seeker-wrapper"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          marginLeft: `${seekProgress * 100}%`,
        }}
      >
        <div className="seeker" />
      </div>
      {/* <style>
        {`
        .seeker-time-display {
          position: absolute;
          transform: translateX(calc(${seekProgress * anchorWidth}px - 50%));
            opacity: ${seekMode ? 1 : 0};
            transition: opacity 0.2s ease-in-out;
            top: -50px;
            font-size: 20px;
            will-change: opacity, transform;
        }
        `}
      </style>
      <div className="seeker-time-display">
        {videoElement ? formatTime(videoElement!.duration * seekProgress, 10) : 0}
      </div> */}
      {/* <div
        className="seeker"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      /> */}
    </>
  )
}

export default MobileTimelineSeeker
